export function licenseCartState(config, cart) {
  const items = cart.items || [];
  const productIds = config.coveringProductIds.map(String);
  const licenseIds = config.coveringLicenseIds.map(String);
  const licensedProductIds = (config.licensedProductIds || config.coveringProductIds).map(String);
  const baseInCart = items.some(item => productIds.includes(String(item.product_id)));
  const licenseInCart = items.some(item =>
    licenseIds.includes(String(item.variant_id)) ||
    (licensedProductIds.includes(String(item.product_id)) && /extended|interactive/i.test(item.variant_title || '')) ||
    (() => {
      try {
        return JSON.parse(item.properties?._mi_covered_products || '[]').map(String).includes(String(config.productId));
      } catch { return false; }
    })()
  );
  return { baseInCart, licenseInCart };
}

export function planLicenseItems(config, cart, wantsLicense, properties = {}) {
  const state = licenseCartState(config, cart);
  const items = [];
  if (!config.licenseOnly && !state.baseInCart) {
    items.push({ id: String(config.baseVariantId), quantity: 1, properties });
  }
  if (wantsLicense && !config.licenseOwned && !state.licenseInCart) {
    items.push({
      id: String(config.licenseVariantId), quantity: 1,
      properties: {
        'Licensed product': config.licenseTitle || config.productTitle,
        _mi_product_id: String(config.licenseProductId || config.productId),
        _mi_covered_products: JSON.stringify(config.coveredProducts)
      }
    });
  }
  return items;
}

const LicenseHTMLElement = globalThis.HTMLElement || class {};

class MiLicenseOption extends LicenseHTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-mi-license-config]').textContent);
    this.form = this.closest('form');
    this.input = this.querySelector('[data-mi-license-toggle]');
    this.cart = { items: [] };
    const { signal } = this.controller;
    this.addEventListener('change', () => this.render(), { signal });
    for (const event of ['cart:update', 'cart:updated', 'cart:refresh', 'mi:cart-changed']) {
      document.addEventListener(event, () => this.queueRefresh(), { signal });
    }
    this.render();
    this.refreshCart();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.refreshTimer);
  }

  queueRefresh() {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refreshCart(), 120);
  }

  async readCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store'
    });
    if (!response.ok) throw new Error(this.config.strings.error);
    return response.json();
  }

  async refreshCart() {
    try { this.syncCart(await this.readCart()); } catch { }
  }

  syncCart(cart) {
    if (!this.config) return;
    this.cart = cart;
    this.render();
  }

  render() {
    if (!this.form || !this.input) return;
    const c = this.config;
    const state = licenseCartState(c, this.cart);
    const licensed = c.licenseOwned || state.licenseInCart;
    this.input.disabled = licensed || !c.licenseAvailable;
    if (licensed) this.input.checked = true;
    const wantsLicense = this.input.checked && !c.licenseOwned;
    for (const field of this.querySelectorAll('[data-mi-license-additional]')) {
      field.disabled = !wantsLicense || licensed || !c.licenseAvailable;
    }
    const status = this.querySelector('[data-mi-license-status]');
    status.textContent = c.licenseOwned ? c.strings.owned : state.licenseInCart ? c.strings.inCart :
      !c.licenseAvailable ? c.strings.unavailable : c.licenseOnly ? c.strings.upgradeNote : c.strings.standard;
    const needsBase = !c.licenseOnly && !state.baseInCart;
    const needsLicense = wantsLicense && !licensed;
    const available = (!needsBase || c.baseAvailable) && (!needsLicense || c.licenseAvailable);
    const disabled = (!needsBase && !needsLicense) || !available;
    const completed = (state.baseInCart && (!wantsLicense || licensed)) || (c.licenseOnly && licensed);
    const buttonText = !available ? c.strings.unavailable : disabled && completed ?
      c.licenseOwned && c.licenseOnly ? c.strings.owned : c.strings.added :
      needsBase ? c.strings.add : c.strings.upgrade;
    const button = this.form.querySelector('button[name="add"]');
    if (button) {
      button.disabled = disabled;
      button.classList.toggle('add-to-cart-button--added', disabled && available && completed);
      button.setAttribute('aria-label', buttonText);
      const target = button.querySelector('[ref="addToCartText"], .add-to-cart-text') || button;
      if (target.textContent.trim() !== buttonText) target.textContent = buttonText;
    }
    const totalPrice = (needsBase ? Number(c.basePrice) : 0) + (needsLicense ? Number(c.licensePrice) : 0);
    const total = this.querySelector('[data-mi-license-total]');
    total.hidden = !wantsLicense || !totalPrice;
    this.querySelector('[data-mi-license-total-price]').textContent = new Intl.NumberFormat(c.locale, {
      style: 'currency', currency: c.currency
    }).format(totalPrice / 100);
    const accelerated = this.form.querySelector('.accelerated-checkout-block');
    if (accelerated) accelerated.hidden = state.baseInCart || !c.baseAvailable;
    const displayingBase = !c.licenseOnly && !(needsLicense && !needsBase);
    const displayedLicensePrice = needsLicense ? Number(c.licensePrice) : 0;
    const displayPrice = (displayingBase ? Number(c.basePrice) : 0) + displayedLicensePrice;
    this.dispatchEvent(new CustomEvent('mi:license-state', {
      bubbles: true,
      detail: { productId: String(c.productId), disabled, buttonText, totalPrice: displayPrice,
        compareAtPrice: displayingBase ? Number(c.compareAtPrice) + displayedLicensePrice : 0 }
    }));
  }

  reportError(message) {
    const error = this.querySelector('[data-mi-license-error]');
    error.textContent = message || this.config.strings.error;
    error.hidden = false;
  }

  async prepareCart(formData) {
    this.querySelector('[data-mi-license-error]').hidden = true;
    const cart = await this.readCart();
    this.syncCart(cart);
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = value;
    }
    const items = planLicenseItems(this.config, cart, this.input.checked, properties);
    if (!items.length) return false;
    for (const key of Array.from(formData.keys())) {
      if (/^(id|quantity)$|^properties\[|^shopify-additional-items\[/.test(key)) formData.delete(key);
    }
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, '1');
      for (const [name, value] of Object.entries(item.properties)) {
        formData.append(`items[${index}][properties][${name}]`, value);
      }
    });
    return true;
  }
}

if (globalThis.customElements && !customElements.get('mi-license-option')) {
  customElements.define('mi-license-option', MiLicenseOption);
}

